import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { BizsolUserInfo } from '@src/api/company/auth/bizsol/bizsolApiClient';
import {
  companyPreValidateApiClient,
  PreValidateErrorResponse,
} from '@src/api/company/auth/companyPreValidateApiClient';
import { companyUserInviteRequestApiClient } from '@src/api/company/auth/companyUserInviteRequestApiClient';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL } from '@src/routes/company';
import { ErrorMessage } from '@src/ui/elements/form';
import Input from '@src/ui/elements/form/Input';
import Select from '@src/ui/elements/form/Select';
import { firstError } from '@src/utils/errorMessageUtils';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { prefectures } from '../../../components/prefectures';

type formValues = {
  companyName: string;
  companyNameKana: string;
  postalCodeFirst: string;
  postalCodeSecond: string;
  prefectureCode: string;
  address: string;
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  email: string;
  birthday: string;
};

function Registration() {
  const location = useLocation();

  const previousFormValues = location.state?.formValues;
  const bizsolUserInfo: BizsolUserInfo = JSON.parse(
    sessionStorage.getItem('bizsolUserInfo') || '{}',
  );
  const [bizsolFamilyName = '', bizsolFirstName = ''] = (bizsolUserInfo?.user_name ?? '').split(
    /[\s\u3000]+/,
  );
  const [formValues, setFormValues] = useState({
    companyName: bizsolUserInfo.company_name ?? '',
    companyNameKana: bizsolUserInfo.company_name_kana ?? '',
    postalCodeFirst: '',
    postalCodeSecond: '',
    prefectureCode: '',
    address: '',
    familyName: bizsolFamilyName ?? '',
    firstName: bizsolFirstName ?? '',
    familyNameKana: '',
    firstNameKana: '',
    email: bizsolUserInfo.email ?? '',
    birthday: '',
    ...previousFormValues,
  });
  const [isCompanyExists, setIsCompanyExists] = useState(false);
  const [isCompanyDisabled, setIsCompanyDisabled] = useState(false);
  const [errors, setErrors] = useState<PreValidateErrorResponse>();
  const [isInviteSending, setIsInviteSending] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const companyAuth = useCompanyAuth();

  const preValidate = async (formValues: formValues) => {
    const formatPostalCode = () => {
      if (!formValues.postalCodeFirst && !formValues.postalCodeSecond) {
        return '';
      }
      return `${formValues.postalCodeFirst}-${formValues.postalCodeSecond}`;
    };

    const response = await companyPreValidateApiClient.preValidate({
      company: {
        name: formValues.companyName,
        name_kana: formValues.companyNameKana,
        postal_code: formatPostalCode(),
        prefecture_code: formValues.prefectureCode,
        address: formValues.address,
      },
      company_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        email: formValues.email,
        birthday: formValues.birthday,
      },
    });

    handleApiResponse(
      response,
      (data) => {
        if (data.is_company_exists) {
          setErrors(undefined);
          return data.is_company_disabled ? setIsCompanyDisabled(true) : setIsCompanyExists(true);
        }

        navigate(COMPANY_URL.REGISTRATION_CREATE, { state: { formValues } });
      },
      (errors: PreValidateErrorResponse) => {
        setErrors(errors);
      },
    );
  };

  const sendInviteRequest = async (formValues: formValues) => {
    setIsInviteSending(true);
    const response = await companyUserInviteRequestApiClient.sendInviteRequest({
      company: {
        name: formValues.companyName,
        name_kana: formValues.companyNameKana,
        postal_code: `${formValues.postalCodeFirst}${formValues.postalCodeSecond}`,
        prefecture_code: formValues.prefectureCode,
        address: formValues.address,
      },
      company_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        email: formValues.email,
        birthday: formValues.birthday,
      },
    });

    handleApiResponse(
      response,
      () => {
        toast(TOAST_MESSAGE.SEND);
      },
      (error) => {
        console.error('Error in invite request:', error);
      },
    );
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='mx-auto max-w-[800px] py-[60px] lg:px-10 lg:pt-24 sm:px-5'>
      <h1 className=' grid h-[42px] place-items-center text-center text-xl font-semibold'>
        企業アカウントの作成
      </h1>
      <form className='mt-10 flex flex-col gap-[26px]'>
        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>企業名</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div
            className='flex'
            ref={(el) => (fieldRefs.current['company.name'] = el)}
          >
            <Input
              size='m'
              type='text'
              placeholder='サンプル株式会社'
              value={formValues.companyName}
              onChange={(e) => setFormValues({ ...formValues, companyName: e.target.value })}
              error={!!errors?.company?.name}
            />
          </div>
          <ErrorMessage>{firstError(errors?.company?.name)}</ErrorMessage>
          <div className='flex items-center gap-3'>
            <label className='text-xs'>
              ※登記上の正式名称を入力してください。スペースや㈱などの略称は使用しないでください。
            </label>
          </div>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label
              htmlFor=''
              className=''
            >
              企業名（フリガナ）
            </label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div
            className='flex'
            ref={(el) => (fieldRefs.current['company.name_kana'] = el)}
          >
            <Input
              size='m'
              type='text'
              placeholder='サンプルカブシキガイシャ'
              value={formValues.companyNameKana}
              onChange={(e) => setFormValues({ ...formValues, companyNameKana: e.target.value })}
              error={!!errors?.company?.name_kana}
            />
          </div>
          <ErrorMessage>{firstError(errors?.company?.name_kana)}</ErrorMessage>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>氏名</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div className='flex gap-4'>
            <div
              className='flex w-full flex-col'
              ref={(el) => (fieldRefs.current['company_user.family_name'] = el)}
            >
              <Input
                size='m'
                type='text'
                placeholder='山田'
                value={formValues.familyName}
                onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                error={!!errors?.company_user?.family_name}
              />
              <ErrorMessage>{firstError(errors?.company_user?.family_name)}</ErrorMessage>
            </div>
            <div
              className='flex w-full flex-col'
              ref={(el) => (fieldRefs.current['company_user.first_name'] = el)}
            >
              <Input
                size='m'
                type='text'
                placeholder='太郎'
                value={formValues.firstName}
                onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                error={!!errors?.company_user?.first_name}
              />
              <ErrorMessage>{firstError(errors?.company_user?.first_name)}</ErrorMessage>
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>氏名（フリガナ）</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div className='flex gap-4'>
            <div
              className='flex w-full flex-col'
              ref={(el) => (fieldRefs.current['company_user.family_name_kana'] = el)}
            >
              <Input
                size='m'
                type='text'
                placeholder='ヤマダ'
                value={formValues.familyNameKana}
                onChange={(e) => setFormValues({ ...formValues, familyNameKana: e.target.value })}
                error={!!errors?.company_user?.family_name_kana}
              />
              <ErrorMessage>{firstError(errors?.company_user?.family_name_kana)}</ErrorMessage>
            </div>
            <div
              className='flex w-full flex-col'
              ref={(el) => (fieldRefs.current['company_user.first_name_kana'] = el)}
            >
              <Input
                size='m'
                type='text'
                placeholder='タロウ'
                value={formValues.firstNameKana}
                onChange={(e) => setFormValues({ ...formValues, firstNameKana: e.target.value })}
                error={!!errors?.company_user?.first_name_kana}
              />
              <ErrorMessage>{firstError(errors?.company_user?.first_name_kana)}</ErrorMessage>
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>メールアドレス</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div
            className='flex'
            ref={(el) => (fieldRefs.current['company_user.email'] = el)}
          >
            <Input
              size='m'
              type='email'
              placeholder='sample@sample.co.jp'
              value={formValues.email}
              onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
              error={!!errors?.company_user?.email}
            />
          </div>
          <ErrorMessage>{firstError(errors?.company_user?.email)}</ErrorMessage>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>生年月日</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div
            className='flex w-[200px]'
            ref={(el) => (fieldRefs.current['company_user.birthday'] = el)}
          >
            <Input
              size='m'
              type='text'
              placeholder='19900101'
              value={formValues.birthday}
              onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
              error={!!errors?.company_user?.birthday}
              inputMode='numeric'
            />
          </div>
          <ErrorMessage>{firstError(errors?.company_user?.birthday)}</ErrorMessage>
        </div>

        <div className='flex flex-col gap-[6px]'>
          <div className='flex items-center gap-3'>
            <label className=''>本社所在地</label>
            <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
              必須
            </div>
          </div>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-[6px]'>
              〒
              <div
                className='w-[100px]'
                ref={(el) => (fieldRefs.current['company.postal_code'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='123'
                  value={formValues.postalCodeFirst}
                  onChange={(e) =>
                    setFormValues({ ...formValues, postalCodeFirst: e.target.value })
                  }
                  error={!!errors?.company?.postal_code}
                />
              </div>
              -
              <div
                className='w-[100px]'
                ref={(el) => (fieldRefs.current['company.postal_code'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='4567'
                  value={formValues.postalCodeSecond}
                  onChange={(e) =>
                    setFormValues({ ...formValues, postalCodeSecond: e.target.value })
                  }
                  error={!!errors?.company?.postal_code}
                />
              </div>
              <ErrorMessage>{firstError(errors?.company?.postal_code)}</ErrorMessage>
            </div>
            <div className='flex gap-4 md:flex-col'>
              <div
                className='w-[164px] flex-[0_0_auto] md:w-full [&>*]:w-full'
                ref={(el) => (fieldRefs.current['company.prefecture_code'] = el)}
              >
                <Select
                  items={prefectures}
                  size='m'
                  data-testid='prefecture-select'
                  initialValue={formValues.prefectureCode}
                  onSelect={(value) => setFormValues({ ...formValues, prefectureCode: value })}
                  error={!!errors?.company?.prefecture_code}
                />
                <ErrorMessage>{firstError(errors?.company?.prefecture_code)}</ErrorMessage>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['company.address'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='〇〇市〇〇区1-2-3'
                  value={formValues.address}
                  onChange={(e) => setFormValues({ ...formValues, address: e.target.value })}
                  error={!!errors?.company?.address}
                />
                <ErrorMessage>{firstError(errors?.company?.address)}</ErrorMessage>
              </div>
            </div>
          </div>
        </div>
        <div className=''>
          <button
            className={`btn-login mx-auto mt-4 max-w-[360px] text-white
              ${
                isCompanyExists || isCompanyDisabled
                  ? 'pointer-events-none bg-gray-200'
                  : 'bg-primary-500'
              }`}
            type='button'
            disabled={isCompanyExists || isCompanyDisabled}
            onClick={() => preValidate(formValues)}
          >
            {companyAuth.profile?.tenant?.requiredUserApproval ? '登録手続きを進める' : '作成する'}
          </button>
        </div>
        {/* --- 企業が無効化されている場合の表示 --- */}
        {isCompanyDisabled && (
          <div className=''>
            <p className='mt-2 grid place-items-center text-center font-medium text-warning-500'>
              企業情報は既に登録されていますが、現在無効化されています。
              <br />
              再度有効化するためには、お問い合わせください。
            </p>
            <a
              href={COMPANY_URL.CONTACT}
              className='btn-login mx-auto mt-8 block max-w-[360px] bg-primary-500 text-center text-white'
            >
              お問い合わせ
            </a>
          </div>
        )}

        {/* --- 企業が存在するが無効化されていない場合の表示 --- */}
        {isCompanyExists && !isCompanyDisabled && (
          <div className=''>
            <p className='mt-2 grid place-items-center text-center font-medium text-warning-500'>
              すでに企業情報が登録されています。
              <br />
              自社のご担当者様へユーザー登録の依頼を実施してください。
            </p>
            <button
              className={`btn-login mx-auto mt-8 max-w-[360px] text-white
                ${isInviteSending ? 'pointer-events-none bg-gray-200' : 'bg-primary-500'}`}
              type='submit'
              onClick={(e) => {
                e.preventDefault();
                sendInviteRequest(formValues);
              }}
              disabled={isInviteSending}
            >
              管理者に招待リクエストを送る
            </button>
          </div>
        )}

        {/* --- 注意書きは、isCompanyDisabled時は非表示 --- */}
        {!isCompanyDisabled && (
          <p className='mt-2 grid h-[42px] place-items-center text-sm'>
            ※企業アカウントを作成した方が、自動的に企業アカウントの管理者となります
          </p>
        )}
      </form>
    </main>
  );
}

export default Registration;
